import React, { useEffect, useState, useMemo } from "react";

// Components
import Breadcrumbs from "../../../components/Breadcrumbs";
import Topbar from "../../../components/Topbar";

// Utils
import { getAlert, parseCurrency } from "../../../../utils/SamuiUtils";
import {
  useDataTableKeyboardNavigation,
  getDataTableRowStyle,
  getDataTableKeyboardInstructions,
} from "../../../../utils/DataTableKeyboardNavigation";

function Main({ masterList = [], name, onPageInsert, onRowSelected, getReport = () => { } }) {
  const [dataMasterList, setDataMasterList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredItems, setFilteredItems] = useState([]);

  const [selectedRowIndex, setSelectedRowIndex] = useState(0);

  useEffect(() => {
    try {
      if (Array.isArray(masterList)) {
        setDataMasterList(masterList);
      } else {
        setDataMasterList([]);
      }
    } catch (error) {
      getAlert("FAILED", error.message);
    }
  }, [masterList]);

  useEffect(() => {
    const lowercased = searchTerm.trim().toLowerCase();
    if (!lowercased) {
      setFilteredItems(dataMasterList);
      return;
    }

    const filtered = dataMasterList.filter((item) => {
      if (!item) return false;
      const valuesToSearch = [
        item.Service_Code,
        item.Service_Name,
        item.Service_Name_EN,
        item.Service_Status === "Y" ? "ใช้งาน" : "ไม่ใช้งาน",
      ];
      return valuesToSearch.some(
        (value) =>
          typeof value === "string" && value.toLowerCase().includes(lowercased)
      );
    });
    setFilteredItems(filtered);
  }, [searchTerm, dataMasterList]);

  useEffect(() => {
    setCurrentPage(1);
    setSelectedRowIndex(0);
  }, [filteredItems]);

  const handlePageChange = (pageNumber) => {
    if (pageNumber < 1) return;
    const totalPages = Math.ceil(filteredItems.length / itemsPerPage) || 1;
    const nextPage = Math.min(pageNumber, totalPages);
    setCurrentPage(nextPage);
    setSelectedRowIndex(0);
  };

  const handleItemsPerPageChange = (event) => {
    const nextValue = parseCurrency(event.target.value);
    setItemsPerPage(nextValue || 50);
    setCurrentPage(1);
    setSelectedRowIndex(0);
  };

  const renderPageNumbers = () => {
    const totalPages = Math.ceil(filteredItems.length / itemsPerPage) || 1;
    const pages = [];
    const maxVisiblePages = 5;
    const range = 2;

    const pageButtonStyle = (isActive) => ({
      borderRadius: "8px",
      border: "1px solid #d1d5db",
      color: isActive ? "#fff" : "#374151",
      backgroundColor: isActive ? "#E13F57" : "#fff",
      fontSize: "14px",
      padding: "6px 12px",
      minWidth: "36px",
      textAlign: "center"
    });

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(
          <li
            key={i}
            className={`page-item ${currentPage === i ? "active" : ""}`}
          >
            <a
              href="#"
              className="page-link"
              onClick={(e) => {
                e.preventDefault();
                handlePageChange(i);
              }}
              style={pageButtonStyle(currentPage === i)}
            >
              {i}
            </a>
          </li>
        );
      }
      return pages;
    }

    pages.push(
      <li
        key={1}
        className={`page-item ${currentPage === 1 ? "active" : ""}`}
      >
        <a
          href="#"
          className="page-link"
          onClick={(e) => {
            e.preventDefault();
            handlePageChange(1);
          }}
          style={pageButtonStyle(currentPage === 1)}
        >
          1
        </a>
      </li>
    );

    if (currentPage - range > 2) {
      pages.push(
        <li key="ellipsis-start" className="page-item">
          <span
            className="page-link"
            style={{
              borderRadius: "8px",
              border: "1px solid #d1d5db",
              color: "#9ca3af",
              backgroundColor: "#fff",
              fontSize: "14px",
              padding: "6px 12px",
              minWidth: "36px",
              textAlign: "center"
            }}
          >
            ...
          </span>
        </li>
      );
    }

    for (
      let i = Math.max(currentPage - range, 2);
      i <= Math.min(currentPage + range, totalPages - 1);
      i++
    ) {
      pages.push(
        <li
          key={i}
          className={`page-item ${currentPage === i ? "active" : ""}`}
        >
          <a
            href="#"
            className="page-link"
            onClick={(e) => {
              e.preventDefault();
              handlePageChange(i);
            }}
            style={pageButtonStyle(currentPage === i)}
          >
            {i}
          </a>
        </li>
      );
    }

    if (currentPage + range < totalPages - 1) {
      pages.push(
        <li key="ellipsis-end" className="page-item">
          <span
            className="page-link"
            style={{
              borderRadius: "8px",
              border: "1px solid #d1d5db",
              color: "#9ca3af",
              backgroundColor: "#fff",
              fontSize: "14px",
              padding: "6px 12px",
              minWidth: "36px",
              textAlign: "center"
            }}
          >
            ...
          </span>
        </li>
      );
    }

    pages.push(
      <li
        key={totalPages}
        className={`page-item ${currentPage === totalPages ? "active" : ""}`}
      >
        <a
          href="#"
          className="page-link"
          onClick={(e) => {
            e.preventDefault();
            handlePageChange(totalPages);
          }}
          style={pageButtonStyle(currentPage === totalPages)}
        >
          {totalPages}
        </a>
      </li>
    );

    return pages;
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = useMemo(() => {
    return filteredItems.slice(indexOfFirstItem, indexOfLastItem);
  }, [filteredItems, indexOfFirstItem, indexOfLastItem]);

  const handleRowSelected = (data) => {
    if (typeof onRowSelected !== "function") {
      return;
    }

    if (data && typeof data === "object") {
      onRowSelected(data);
      return;
    }

    const foundItem = filteredItems.find(
      (item) => item?.Service_Id === data
    );

    if (foundItem) {
      onRowSelected(foundItem);
    }
  };

  const fieldMappings = {
    no: "Service_Id",
  };

  useDataTableKeyboardNavigation(
    true,
    currentItems,
    selectedRowIndex,
    setSelectedRowIndex,
    handleRowSelected,
    fieldMappings,
    currentPage,
    handlePageChange,
    filteredItems.length,
    itemsPerPage
  );

  return (
    <>
      <Topbar
        breadcrumbs={[
          { label: name },
        ]}
      />
      <div className="page-header d-flex justify-content-between align-items-center mb-3">
        <Breadcrumbs
          page={name}
          items={[
            { name: "ข้อมูลตั้งต้น", url: "/initialdata" },
            { name: name, url: "/service" },
          ]}
        />
      </div>

      {/* Search and Action Bar */}
      <div className="card shadow-sm mb-3 border-0" style={{ borderRadius: "12px", overflow: "hidden" }}>
        <div className="card-body p-3">
          <div className="row g-3 align-items-center">
            <div className="col-md-6 col-lg-4">
              <div className="position-relative">
                <input
                  type="text"
                  placeholder="ค้นหาข้อมูลบริการ..."
                  className="form-control ps-5"
                  style={{
                    borderRadius: "10px",
                    border: "1px solid #e5e7eb",
                    padding: "10px 10px 10px 45px",
                    fontSize: "14px"
                  }}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <i
                  className="fas fa-search position-absolute"
                  style={{
                    left: "16px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#9ca3af"
                  }}
                ></i>
              </div>
            </div>
            <div className="col-md-6 col-lg-8 d-flex justify-content-end">
              <button
                onClick={onPageInsert}
                className="btn text-white d-flex align-items-center gap-2"
                style={{
                  backgroundColor: "#E13F57",
                  borderRadius: "10px",
                  padding: "10px 24px",
                  fontWeight: "500",
                  border: "none",
                  boxShadow: "0 2px 8px rgba(225, 63, 87, 0.2)",
                  transition: "all 0.3s ease"
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = "#c9354a";
                  e.currentTarget.style.transform = "translateY(-1px)";
                  e.currentTarget.style.boxShadow = "0 4px 12px rgba(225, 63, 87, 0.3)";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = "#E13F57";
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 2px 8px rgba(225, 63, 87, 0.2)";
                }}
                type="button"
              >
                <i className="fa fa-plus"></i>
                <span>สร้าง{name}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Data Table Card */}
      <div className="card shadow-sm border-0" style={{ borderRadius: "12px", overflow: "hidden" }}>
        <div className="card-body p-0">
          <div className="table-responsive" style={{ maxHeight: "600px", overflowY: "auto" }}>
            <table className="table table-hover mb-0" style={{ fontSize: "14px" }}>
              <thead style={{
                backgroundColor: "rgb(225,63,87)",
                color: "#fff",
                position: "sticky",
                top: 0,
                zIndex: 101,
                borderBottom: "2px solid rgba(0,0,0,0.08)"
              }}>
                <tr>
                  <th className="text-center py-3" style={{ width: "5%", fontWeight: "600", color: "#fff" }}>#</th>
                  <th className="py-3" style={{ width: "30%", fontWeight: "600", color: "#fff" }}>ชื่อบริการ</th>
                  <th className="py-3" style={{ width: "25%", fontWeight: "600", color: "#fff" }}>ชื่อบริการ (EN)</th>
                  <th className="py-3" style={{ width: "30%", fontWeight: "600", color: "#fff" }}>รายละเอียดบริการ</th>
                  <th className="text-center py-3" style={{ width: "6%", fontWeight: "600", color: "#fff" }}>สถานะ</th>
                  <th className="text-center py-3" style={{ width: "2%", fontWeight: "600", color: "#fff" }}>แก้ไข</th>
                  <th className="text-center py-3" style={{ width: "2%", fontWeight: "600", color: "#fff" }}>พิมพ์</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.length > 0 ? (
                  currentItems.map((item, index) => (
                    <tr
                      key={item.Service_Id || index}
                      style={{
                        ...getDataTableRowStyle(index, selectedRowIndex),
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        borderBottom: "1px solid #f3f4f6"
                      }}
                      onClick={() => setSelectedRowIndex(index)}
                      onMouseEnter={(e) => {
                        if (index !== selectedRowIndex) {
                          e.currentTarget.style.backgroundColor = "#f9fafb";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (index !== selectedRowIndex) {
                          e.currentTarget.style.backgroundColor = "";
                        }
                      }}
                    >
                      <td className="text-center align-top py-2" style={{ verticalAlign: 'top' }}>
                        {indexOfFirstItem + index + 1}
                      </td>
                      <td className="align-top py-2" style={{ color: "#111827", verticalAlign: 'top', maxWidth: '100%', paddingRight: '8px' }}>
                        <div>{item.Service_Name || "-"}</div>
                      </td>
                      <td className="align-top py-2" style={{ color: "#111827", verticalAlign: 'top', maxWidth: '100%', paddingRight: '8px' }}>
                        <div>{item.Service_Name_EN || "-"}</div>
                      </td>
                      <td className="align-top py-2" style={{ verticalAlign: 'top', paddingRight: '12px' }} title={item.Service_Desc || ""}>
                        <div style={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'normal'
                        }}>{item.Service_Desc || '-'}</div>
                      </td>
                      <td className="text-center align-middle py-3">
                        <span
                          className="badge"
                          style={{
                            backgroundColor: item.Service_Status === "Y" ? "#dcfce7" : "#fee2e2",
                            color: item.Service_Status === "Y" ? "#166534" : "#991b1b",
                            padding: "6px 12px",
                            borderRadius: "20px",
                            fontWeight: "500",
                            fontSize: "12px"
                          }}
                        >
                          {item.Service_Status === "Y" ? "ใช้งาน" : "ไม่ใช้งาน"}
                        </span>
                      </td>
                      <td className="text-center align-middle py-3">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRowSelected(item);
                          }}
                          className="btn btn-sm"
                          style={{
                            width: "32px",
                            height: "32px",
                            borderRadius: "8px",
                            border: "none",
                            backgroundColor: "#fff3e0",
                            color: "#EF6C00",
                            transition: "all 0.2s ease"
                          }}
                          onMouseOver={(e) => {
                            e.currentTarget.style.backgroundColor = "#EF6C00";
                            e.currentTarget.style.color = "#fff";
                          }}
                          onMouseOut={(e) => {
                            e.currentTarget.style.backgroundColor = "#fff3e0";
                            e.currentTarget.style.color = "#EF6C00";
                          }}
                        >
                          <i className="fas fa-edit"></i>
                        </button>
                      </td>
                      <td className="text-center align-middle py-3">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            try {
                              getReport(item.Service_Id);
                            } catch (err) {
                              console.error('getReport failed', err);
                            }
                          }}
                          className="btn btn-sm"
                          title="พิมพ์"
                          style={{
                            width: "32px",
                            height: "32px",
                            borderRadius: "8px",
                            border: "none",
                            backgroundColor: "#e3f2fd",
                            color: "#1976d2",
                            transition: "all 0.2s ease"
                          }}
                          onMouseOver={(e) => {
                            e.currentTarget.style.backgroundColor = "#1976d2";
                            e.currentTarget.style.color = "#fff";
                          }}
                          onMouseOut={(e) => {
                            e.currentTarget.style.backgroundColor = "#e3f2fd";
                            e.currentTarget.style.color = "#1976d2";
                          }}
                        >
                          <i className="fas fa-print"></i>
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center py-5">
                      <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: "200px" }}>
                        <i className="fas fa-inbox" style={{ fontSize: "48px", color: "#d1d5db", marginBottom: "16px" }}></i>
                        <h5 style={{ color: "#6b7280", fontWeight: "500" }}>ไม่พบข้อมูล</h5>
                        <p style={{ color: "#9ca3af", fontSize: "14px", marginTop: "8px" }}>
                          ไม่มีข้อมูลบริการในระบบ
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {currentItems.length > 0 && (
            <>
              <div className="px-4 pt-3 pb-2" style={{ backgroundColor: "#f9fafb", borderTop: "1px solid #e5e7eb" }}>
                <small className="text-muted" style={{ fontSize: "13px" }}>
                  {getDataTableKeyboardInstructions(false, true)}
                </small>
              </div>

              <div className="px-4 py-3" style={{ backgroundColor: "#f9fafb" }}>
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
                  <div className="d-flex align-items-center gap-2">
                    <label htmlFor="itemsPerPage" className="mb-0" style={{ fontSize: "14px", color: "#6b7280" }}>
                      แสดง:
                    </label>
                    <select
                      id="itemsPerPage"
                      value={itemsPerPage}
                      onChange={handleItemsPerPageChange}
                      className="form-select"
                      style={{
                        width: "auto",
                        minWidth: "80px",
                        fontSize: "14px",
                        borderRadius: "8px",
                        border: "1px solid #d1d5db",
                        padding: "6px 12px"
                      }}
                    >
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                      <option value={150}>150</option>
                      <option value={dataMasterList.length}>ทั้งหมด</option>
                    </select>
                    <span style={{ fontSize: "14px", color: "#6b7280" }}>
                      รายการ (ทั้งหมด {filteredItems.length} รายการ)
                    </span>
                  </div>

                  <nav>
                    <ul className="pagination mb-0" style={{ gap: "4px" }}>
                      <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                        <a
                          href="#"
                          className="page-link"
                          onClick={(e) => {
                            e.preventDefault();
                            handlePageChange(currentPage - 1);
                          }}
                          style={{
                            borderRadius: "8px",
                            border: "1px solid #d1d5db",
                            color: "#374151",
                            fontSize: "14px",
                            padding: "6px 12px"
                          }}
                        >
                          ก่อนหน้า
                        </a>
                      </li>
                      {renderPageNumbers()}
                      <li
                        className={`page-item ${currentPage === Math.ceil(filteredItems.length / itemsPerPage) ||
                          filteredItems.length === 0
                          ? "disabled"
                          : ""
                          }`}
                      >
                        <a
                          href="#"
                          className="page-link"
                          onClick={(e) => {
                            e.preventDefault();
                            handlePageChange(currentPage + 1);
                          }}
                          style={{
                            borderRadius: "8px",
                            border: "1px solid #d1d5db",
                            color: "#374151",
                            fontSize: "14px",
                            padding: "6px 12px"
                          }}
                        >
                          ถัดไป
                        </a>
                      </li>
                    </ul>
                  </nav>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

export default Main;
